const API_URL = process.env.NEXT_PUBLIC_API_URL || ''

/* ───── Tipler (UsStockDetailClient kontratı — değiştirilmiyor) ───── */

export interface UsQuote {
  symbol: string
  price: number
  open: number
  dayHigh: number
  dayLow: number
  previousClose: number
  change: number
  changePercent: number
  volume: number
  avgVolume: number
  marketCap: number
  peRatio: number
  week52High: number
  week52Low: number
  lastUpdate: number
}

export interface UsChartPoint {
  time: number
  value: number
}

export interface PeriodChange {
  monthly: number
  yearly: number
}

/* ───── Backend ham yanıt tipleri (sadece bu dosya içinde) ───── */

interface MarketQuoteRaw {
  symbol: string
  name?: string
  currency?: string
  date?: string
  open: number
  high: number
  low: number
  close: number
  previousClose: number | null
  volume: number
  diff: number
  changePercent: number
  monthly: number | null
  yearly: number | null
}

interface StockChartRaw {
  symbol: string
  count: number
  data: Array<{
    time: string
    open: number
    high: number
    low: number
    close: number
    volume: number
  }>
}

/* ───── Yardımcı: Backend MarketQuote → UsQuote map ───── */

function mapToUsQuote(raw: MarketQuoteRaw): UsQuote {
  const updateMs = raw.date ? Date.parse(raw.date) : NaN
  return {
    symbol: raw.symbol,
    price: raw.close,
    open: raw.open,
    dayHigh: raw.high,
    dayLow: raw.low,
    previousClose: raw.previousClose ?? 0,
    change: raw.diff,
    changePercent: raw.changePercent,
    volume: raw.volume,
    // Backend mevcut endpoint'inde bu alanları döndürmüyor — 0 = "yok"
    avgVolume: 0,
    marketCap: 0,
    peRatio: 0,
    week52High: 0,
    week52Low: 0,
    lastUpdate: isNaN(updateMs) ? 0 : Math.floor(updateMs / 1000),
  }
}

/* ───── Quote (Fiyat) — backend: /api/market/quotes ───── */

export async function fetchUsQuotes(symbols: string[]): Promise<Record<string, UsQuote>> {
  if (symbols.length === 0) return {}
  try {
    const url = `${API_URL}/api/market/quotes?symbols=${symbols.join(',')}`
    const res = await fetch(url)
    if (!res.ok) return {}
    const raw: MarketQuoteRaw[] = await res.json()
    if (!Array.isArray(raw)) return {}
    return raw.reduce<Record<string, UsQuote>>((acc, item) => {
      if (item?.symbol) acc[item.symbol] = mapToUsQuote(item)
      return acc
    }, {})
  } catch {
    return {}
  }
}

/* ───── Tarihsel Veri (Grafik) — backend: /api/stock/chart/{symbol} ───── */

export async function fetchUsChartData(
  symbol: string,
  period: string,
): Promise<UsChartPoint[]> {
  try {
    const url = `${API_URL}/api/stock/chart/${encodeURIComponent(symbol)}?period=${encodeURIComponent(period)}`
    const res = await fetch(url)
    if (!res.ok) return []
    const raw: StockChartRaw = await res.json()
    if (!raw || !Array.isArray(raw.data)) return []
    return raw.data
      .map(p => {
        const ms = Date.parse(p.time)
        return {
          time: isNaN(ms) ? 0 : Math.floor(ms / 1000),
          value: p.close,
        }
      })
      .filter(p => p.time > 0)
  } catch {
    return []
  }
}

/* ───── Aylık / Yıllık Değişim — aynı batch quote yanıtından türetiliyor ───── */

export async function fetchUsPeriodChanges(
  symbols: string[],
): Promise<Record<string, PeriodChange>> {
  if (symbols.length === 0) return {}
  try {
    const url = `${API_URL}/api/market/quotes?symbols=${symbols.join(',')}`
    const res = await fetch(url)
    if (!res.ok) return {}
    const raw: MarketQuoteRaw[] = await res.json()
    if (!Array.isArray(raw)) return {}
    return raw.reduce<Record<string, PeriodChange>>((acc, item) => {
      if (item?.symbol) {
        acc[item.symbol] = {
          monthly: item.monthly ?? 0,
          yearly: item.yearly ?? 0,
        }
      }
      return acc
    }, {})
  } catch {
    return {}
  }
}
